import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api/client';
import { showToast } from '../../utils/toast';

const WebullConnect: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [lastPayload, setLastPayload] = useState<unknown>(null);
  const [statusNote, setStatusNote] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const webull = params.get('webull');
    if (!webull) return;

    if (webull === 'connected') {
      setStatusNote('Webull connected. Tokens saved on the API server (webull_oauth_tokens.json). Check server logs.');
      showToast('success', 'Webull connected — tokens saved on server');
      console.log('[Webull OAuth] returned from API callback: connected');
    } else {
      const reason = params.get('reason') || 'unknown';
      setStatusNote(`Webull connect failed (${reason}). Check API server logs.`);
      showToast('error', `Webull connect failed: ${reason}`);
      console.error('[Webull OAuth] returned from API callback: error', reason);
    }
  }, [location.search]);

  const handleConnect = async () => {
    setLoading(true);
    setLastPayload(null);
    setStatusNote(null);
    try {
      const res = await apiClient.get('/brokerage/webull-oauth/authorize');
      const data = res.data;
      console.log('[Webull OAuth] authorize response', data);
      setLastPayload(data);

      const authorizeUrl = data?.authorizeUrl;
      if (!authorizeUrl) {
        showToast('error', 'No Webull authorize URL returned');
        return;
      }

      if (data.state) {
        sessionStorage.setItem('webull_oauth_state', String(data.state));
      }

      showToast('success', 'Redirecting to Webull…');
      window.location.href = authorizeUrl;
    } catch (err: any) {
      console.error('[Webull OAuth] authorize failed', err?.response?.data || err);
      setLastPayload(err?.response?.data || { error: err?.message || 'Failed' });
      showToast('error', err?.response?.data?.error || err?.message || 'Failed to start Webull connect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Connect Webull</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Master-only sandbox OAuth. Callback goes to the API domain registered with Webull
          (<code className="text-xs">api.test.thetradepilot.ai/webull/callback</code>), then returns here.
        </p>
      </div>

      {statusNote && (
        <p className="text-sm text-foreground rounded-xl border border-border bg-card p-3">{statusNote}</p>
      )}

      <button
        type="button"
        onClick={handleConnect}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {loading ? 'Starting…' : 'Connect Webull'}
      </button>

      {lastPayload != null && (
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="mb-2 text-sm font-medium text-foreground">Last response (also in console)</p>
          <pre className="max-h-[480px] overflow-auto text-xs text-muted-foreground whitespace-pre-wrap break-all">
            {JSON.stringify(lastPayload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default WebullConnect;
